import React, {
  memo,
  useRef,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from "react";
import { Container, useApp } from "@pixi/react";
import { Settings } from "../../../settings";
import FreeSpinBasicReel from "../FreeSpinBasicReel/FreeSpinBasicReel";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import FreeSpinBasicReelSetMask from "../FreeSpinBasicReelSetMask/FreeSpinBasicReelSetMask";
import SpineTextureExtractor from "../../../utils/SpineTextureExtractor";
import SpineAnimationData from "../../../utils/SpineAnimationData";
import FontGenerator from "../../../utils/FontGenerator";
import { useSelector } from "react-redux";
import {
  selectCurrentRound,
  selectAutoPlayActive,
  selectFreeSpinsCurrentRound,
} from "../../../State/selectors";
import { useFreeSpinBasicLandingAnimations } from "../../../Hooks/useFreeSpinBasicLandingAnimations";
import { useReelControl } from "../../../Contexts/ReelControlContext";

/**
 * FreeSpinBasicReelSet — the Mad Labs bonus board.
 *
 * A copy of the base game's ReelSet, mounted into the bonus_gameview spine
 * instead of game_view. It spins with the same simple spin the base game uses,
 * which is the whole point of the copy: nothing new to learn, and nothing the
 * base board depends on can be broken by changing it.
 *
 * What differs from ReelSet, and nothing else:
 *   - its own ref arrays (freeSpinBasic*), so both boards can live in the tree
 *   - its own landing hook, for the same reason
 *   - NO visibility gate: the bonus scene shows and hides this board
 */

// ── The board these reels are BUILT with ──
//
// Module level so an ordinary Redux update cannot re-render this reelset, which
// is why it exists. It used to be "set once, never changes" — and that is what
// made a second bonus wear the first one's screen.
//
// The reels are BUILT from this, and the built symbols are what shows wherever a
// later repaint does not reach. Set once per page load, every bonus after the
// first was therefore built from whatever board happened to be on screen when
// the game loaded, not from the spin that triggered it. Two reels came out
// character-identical across two different bonuses, which is what gave it away.
//
// Now it follows the ARRIVAL BOARD: every bonus is built from the base-game
// screen that triggered THAT bonus. The load-time base round stays as the
// fallback, because this has to have a value before any free spin exists.
let cachedReels = null;

const FreeSpinBasicReelSet = memo(
  ({ symbolWidth, symbolHeight, dividerDimensions, reelSpacing }) => {
    const app = useApp();
    const {
      freeSpinBasicReelRefs: reelRefs,
      freeSpinBasicReelSetMaskRef: reelSetMaskRef,
    } = useReelRefs();
    const { triggerReelLandingAnimations } = useFreeSpinBasicLandingAnimations();
    // TEMPORARY: still the base game's control context, for the turbo flag
    // only. It moves to the free-spin control context when that lands.
    const { spacebarTurboActive } = useReelControl();
    // ── BUILD-TIME data only, and deliberately the BASE round ──
    //
    // This is used once, to know how many reels to render and what symbols to
    // build them with. It must be available AT LOAD, and the free-spin round
    // is null until the first freespin response — so seeding from it left this
    // whole component returning null, the reels unregistered, and the round
    // hanging on its first spin with nothing to start.
    //
    // The board this reelset SHOWS does not come from here. Each
    // FreeSpinBasicReel subscribes to the free-spin round itself and calls
    // updateReels, which no-ops while that round is still null. So the board
    // exists from load wearing the base game's symbols, and swaps to its own
    // the moment the first free spin lands — exactly how the old
    // FreeSpinReelSet did it.
    const currentRound = useSelector(selectCurrentRound);
    // The room's own round. Read here for ONE reason: to notice the arrival
    // board and rebuild from it — see the cache below.
    const freeSpinsRound = useSelector(selectFreeSpinsCurrentRound);
    const autoPlayActive = useSelector(selectAutoPlayActive);
    const maskRef = useRef(null);
    // ONE container for every reel's sprites. Symbols can only sort against
    // siblings, so per-reel containers made cross-reel priority impossible —
    // same reason the spine overlay was flattened.
    const sharedSymbolsContainerRef = useRef(null);
    const [maskReady, setMaskReady] = useState(false);

    // ── A NEW bonus is built from the screen that triggered IT ──
    //
    // `isTriggerBoard` is set by arrivalBoardFrom and by nothing else, so it
    // marks exactly one thing: the base-game board this bonus is opening on,
    // already stripped of its wins (`wins: []`, `win: 0`) so nothing on it can
    // celebrate. That is the board to build from, every time.
    //
    // Compared by REFERENCE, so this assigns once per bonus rather than on
    // every render — the same frozen-reference discipline the fallback below
    // was written for.
    if (
      freeSpinsRound?.isTriggerBoard &&
      Array.isArray(freeSpinsRound.reels) &&
      cachedReels !== freeSpinsRound.reels
    ) {
      cachedReels = freeSpinsRound.reels;
    }

    // Fallback, and only that: at load there is no free-spins round at all, and
    // this component must still know how many reels to render and with what.
    if (!cachedReels && currentRound?.reels) {
      cachedReels = currentRound.reels;
    }

    // Use cached reels, sliced to configured REEL_COUNT (server may send more)
    const reels = cachedReels
      ? cachedReels.slice(0, Settings.REEL_COUNT)
      : null;

    // Callback ref to track when mask is ready
    const setMaskRef = useCallback(
      (node) => {
        maskRef.current = node;
        // Share mask with other components via context
        if (reelSetMaskRef) reelSetMaskRef.current = node;
        if (node && !maskReady) {
          setMaskReady(true);
        }
      },
      [maskReady, reelSetMaskRef],
    );

    const triggerLandingRef = useRef(triggerReelLandingAnimations);
    useEffect(() => {
      triggerLandingRef.current = triggerReelLandingAnimations;
    }, [triggerReelLandingAnimations]);

    // Batch landing animations - collect all reel data and trigger once when last reel stops
    // ONLY during turbo mode for optimal performance
    const pendingLandingAnimations = useRef(new Map()); // Map<reelIndex, reelData>
    const rafIdRef = useRef(null);

    const onReelStop = useCallback(
      (reelIndex, reelData) => {
        if (!reelData) return;

        // Check if turbo mode is active (spacebar turbo or autoplay)
        const isTurboMode = spacebarTurboActive || autoPlayActive;

        // TURBO MODE: Batch all landing animations until last reel stops
        if (isTurboMode) {
          // Store this reel's data
          pendingLandingAnimations.current.set(reelIndex, reelData);

          // If this is the last reel (index 4), trigger all landing animations at once
          if (reelIndex === Settings.REEL_COUNT - 1) {
            // Cancel any pending RAF
            if (rafIdRef.current !== null) {
              cancelAnimationFrame(rafIdRef.current);
            }

            // Collect all texture hide operations from all reels
            const allTextureHideOps = [];

            try {
              // Process all reels in order - show all spine animations first
              for (let i = 0; i < Settings.REEL_COUNT; i++) {
                const data = pendingLandingAnimations.current.get(i);
                if (data) {
                  // skipRAF=true to prevent individual RAF calls
                  const hideOps = triggerLandingRef.current?.(
                    i,
                    data,
                    null,
                    true,
                  );
                  if (hideOps && hideOps.length > 0) {
                    allTextureHideOps.push(...hideOps);
                  }
                }
              }
            } catch (err) {
              // Fail silently
            }

            // Single RAF to hide all textures at once (after all spine animations are set up)
            if (allTextureHideOps.length > 0) {
              rafIdRef.current = requestAnimationFrame(() => {
                allTextureHideOps.forEach(({ reelController, symbolIndex }) => {
                  if (reelController.hideSymbolTexture) {
                    reelController.hideSymbolTexture(symbolIndex);
                  }
                });

                // Clear pending animations
                pendingLandingAnimations.current.clear();
                rafIdRef.current = null;
              });
            } else {
              // No textures to hide, just clear
              pendingLandingAnimations.current.clear();
            }
          }
        } else {
          // REGULAR/MANUAL MODE: Trigger landing animations immediately per reel
          // This provides instant visual feedback for manual stops
          try {
            triggerLandingRef.current?.(reelIndex, reelData);
          } catch (err) {
            // Fail silently
          }
        }
      },
      [spacebarTurboActive, autoPlayActive],
    );

    // ✅ Memoized selector (no unnecessary re-renders)

    // Initialize and consume spine-extracted textures + generated fonts
    const { symbolsSpriteSheet, symbolsSpriteSheetBlurred } = useMemo(() => {
      if (app?.renderer) {
        SpineTextureExtractor.initialize(app.renderer);
        SpineAnimationData.initialize();
        FontGenerator.initialize(app.renderer);
      }
      return {
        symbolsSpriteSheet: SpineTextureExtractor.getSymbols(),
        symbolsSpriteSheetBlurred: SpineTextureExtractor.getBlurredSymbols(),
      };
    }, [app?.renderer]);

    // Cache symbols data
    const reelSymbolsData = useMemo(() => {
      if (!symbolsSpriteSheet) return null;
      return (
        symbolsSpriteSheet.data?.frames || symbolsSpriteSheet.textures || null
      );
    }, [symbolsSpriteSheet]);

    // Memoize reel container Y position (constant for all reels)
    const reelContainerY = useMemo(
      () =>
        -symbolHeight * (1.5 * Settings.SYMBOLS_VISIBLE - 0.5) - symbolHeight,
      [symbolHeight],
    );

    // Memoize reel container X positions (one per reel)
    const reelPositions = useMemo(() => {
      const centerOffset = (Settings.REEL_COUNT - 1) / 2;
      return reels
        ? reels.map((_, index) => ({
            x: reelSpacing * (index - centerOffset),
            y: reelContainerY,
          }))
        : [];
    }, [reels, reelSpacing, reelContainerY]);

    // Guard: Don't render until reels and sprite textures are ready
    // Blurred textures are optional (controlled by SPIN.BLURRED_TEXTURES_ENABLED)
    if (
      !reels ||
      !Array.isArray(reels) ||
      reels.length === 0 ||
      !symbolsSpriteSheet?.textures
    ) {
      return null;
    }

    return (
      <>
        <FreeSpinBasicReelSetMask
          ref={setMaskRef}
          symbolWidth={symbolWidth}
          symbolHeight={symbolHeight}
          dividerDimensions={dividerDimensions}
        />

        {maskReady && (
          <Container
            name="fs_reelset"
            mask={maskRef.current}
            x={0}
            y={0}
            zIndex={1}
            sortableChildren
          >
            {/* Every reel's sprites, as siblings. Rendered first so its ref is
                attached before any FreeSpinBasicReel effect builds a controller against it. */}
            <Container
              ref={sharedSymbolsContainerRef}
              name="fs_reel_symbols"
              x={0}
              y={reelContainerY}
              zIndex={1}
              sortableChildren
            />

            {reels.map((reel, index) => {
              const position = reelPositions[index];
              if (!position) return null;

              // Holds only the tension dim overlay now. zIndex keeps dimming
              // above the symbols, which used to come free from child order.
              return (
                <Container
                  key={index}
                  name={`fs_reel_container_${index}`}
                  x={position.x}
                  y={position.y}
                  zIndex={100}
                >
                  <FreeSpinBasicReel
                    ref={(el) => (reelRefs.current[index] = el)}
                    reel={reel}
                    index={index}
                    symbolWidth={symbolWidth}
                    symbolHeight={symbolHeight}
                    symbolsSpriteSheet={symbolsSpriteSheet}
                    symbolsSpriteSheetBlurred={symbolsSpriteSheetBlurred}
                    reelSymbolsData={reelSymbolsData}
                    onReelStop={onReelStop}
                    sharedSymbolsContainerRef={sharedSymbolsContainerRef}
                    reelXOffset={position.x}
                  />
                </Container>
              );
            })}
          </Container>
        )}
      </>
    );
  },
);

export default FreeSpinBasicReelSet;
